/**
 * MontaTech v2 - usePing Hook
 *
 * Ping periódico ao backend (GET /health) para medir latência.
 * Usa mediana dos últimos N pings para suavizar spikes de fetch.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL, HEALTHCHECK_PATH } from '../../../../config';
import { getTenantId } from '../../../../utils/getTenantId';

export type PingStatus = 'unknown' | 'online' | 'slow' | 'offline';

export interface PingState {
  status: PingStatus;
  latencyMs: number | null;
}

const PING_INTERVAL_MS = 15_000; // 15 segundos
const PING_TIMEOUT_MS = 5_000;   // 5 segundos timeout
const SLOW_THRESHOLD_MS = 250;   // > 250ms = slow
const HISTORY_SIZE = 5;          // mediana dos últimos 5 pings

/**
 * Calcula a mediana de um array de números.
 */
function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
}

/**
 * Faz um ping raw ao backend e retorna latência em ms ou null se falhou.
 */
async function pingBackendRaw(): Promise<number | null> {
  const path = HEALTHCHECK_PATH || '/health';
  const tenantId = getTenantId();
  const start = performance.now();

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);

    const res = await fetch(`${API_URL}${path}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
      },
      mode: 'cors',
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (!res.ok) return null;
    return Math.round(performance.now() - start);
  } catch {
    return null;
  }
}

/**
 * Hook que faz ping periódico ao backend.
 * Exibe mediana dos últimos pings para suavizar spikes.
 */
export function usePing(): PingState {
  const [state, setState] = useState<PingState>({ status: 'unknown', latencyMs: null });
  const historyRef = useRef<number[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const doPing = useCallback(async () => {
    const rawMs = await pingBackendRaw();

    if (rawMs === null) {
      // Offline — limpa histórico
      historyRef.current = [];
      setState({ status: 'offline', latencyMs: null });
      return;
    }

    // Adicionar ao histórico (manter últimos N)
    const history = historyRef.current;
    history.push(rawMs);
    if (history.length > HISTORY_SIZE) history.shift();

    const smoothMs = median(history);

    setState({
      status: smoothMs > SLOW_THRESHOLD_MS ? 'slow' : 'online',
      latencyMs: smoothMs,
    });
  }, []);

  useEffect(() => {
    doPing();
    intervalRef.current = setInterval(doPing, PING_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [doPing]);

  return state;
}
